// Sales Demos — internal-only page for creating demo Brands under the
// "Sales Demos" Advertiser and pulling records via Apify. Access is
// gated server-side: every /api/sales-demos endpoint 403s if the
// caller isn't scoped to the Sales Demos advertiser.
//
// A demo Brand is a normal Brand with Brand.isDemo=true and an
// Apify-config subdoc (IG handle + Shopify store URL). "Sync from
// Apify" pulls public records and feeds them into the same detect +
// catalog pipeline real customer Brands use.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box, Card, CardBody, VStack, HStack, Heading, Text, Button, Badge,
  Input, useToast, Spinner, SimpleGrid, FormControl, FormLabel,
  Radio, RadioGroup, Progress, Divider
} from '@chakra-ui/react';
import { PageHeader } from '../../shell/PageHeader';
import { apiJson } from '../../auth/apiFetch';
import { useBrand } from '../../brand/BrandContext';
import type { OperationRunDto } from '../../shell/ActivityBar';

// Catalog ingest method (mirrors Brand.apifyDemo.method /
// salesDemosService.normalizeMethod on the backend).
type DemoMethod = 'shopify-direct' | 'apify' | 'generic-sitemap';

type DemoBrand = {
  _id:           string;
  name:          string;
  nameNormalized: string;
  logoUrl:       string | null;
  websiteUrl:    string | null;
  apifyDemo: {
    igHandle:     string | null;
    shopifyUrl:   string | null;
    lastSyncedAt: string | null;
    aborted?:     boolean;
    method?:      DemoMethod;
  };
  // Live count of queued+processing DetectRuns for this brand. Non-zero
  // means the detect pipeline still has work to do — abort is
  // available even after a page navigation dropped local state.
  inFlightDetectRuns: number;
  // Ingested totals (from the brand-list aggregations): catalog products
  // and IG posts. Shown on the card so completed syncs report what landed.
  productCount?: number;
  postCount?:    number;
  // Products that carried structured review data — numerator for the
  // "review coverage %" badge (reviewedProductCount / productCount).
  reviewedProductCount?: number;
  createdAt: string;
};

type BootstrapStatus = {
  canBootstrap:  boolean;
  alreadyMember: boolean;
  advertiserId:  string | null;
};

export function SalesDemosPage() {
  const toast = useToast();
  const { setActiveAdvertiser } = useBrand();

  const [status, setStatus]     = useState<BootstrapStatus | null>(null);
  const [bootstrapping, setBootstrapping] = useState(false);

  const [brands, setBrands]     = useState<DemoBrand[]>([]);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState<string | null>(null);
  // Distinguishes "backend 403'd because I'm not in the Sales Demos
  // scope" from a real error — the former shows the bootstrap panel,
  // the latter shows a red error card.
  const [outOfScope, setOutOfScope] = useState(false);

  const [newName, setNewName]         = useState('');
  const [newIg, setNewIg]             = useState('');
  const [newShopify, setNewShopify]   = useState('');
  const [newMethod, setNewMethod]     = useState<DemoMethod>('shopify-direct');
  const [creating, setCreating]       = useState(false);

  // Per-brand sync state, keyed by brand id. The background sync
  // completes when Brand.apifyDemo.lastSyncedAt bumps; the poller
  // clears `syncing[id]` at that point (or on timeout).
  const [syncing, setSyncing]     = useState<Record<string, boolean>>({});
  // Per-brand "Enrich" request in flight (just for the button spinner —
  // the actual enrichment progress shows in the Activity log + the global
  // dock, since it's a background OperationRun).
  const [enriching, setEnriching] = useState<Record<string, boolean>>({});

  // Poller cancellation signal, keyed by brand id. Flipping to true
  // makes the next poller tick exit silently — used when the operator
  // clicks Abort so the "Sync complete" toast doesn't fire later.
  const abortRef = useRef<Record<string, boolean>>({});

  // silent: background refreshes (the 8s interval, sync pollers) must
  // not flip the full-page spinner — that unmounts every brand card
  // (and its Abort/Sync buttons) once per poll. They also must not
  // replace a working list with the error card on a transient blip;
  // stale cards beat no cards.
  const loadBrands = useCallback(async (opts: { silent?: boolean } = {}) => {
    if (!opts.silent) {
      setLoading(true);
      setError(null);
      setOutOfScope(false);
    }
    try {
      const data = await apiJson<{ brands: DemoBrand[] }>('/api/sales-demos/brands');
      setBrands(data.brands || []);
      setError(null);
    } catch (err) {
      const msg = (err as Error).message;
      // Backend 403 message when the caller isn't scoped to Sales Demos.
      // Detected by string match rather than status — apiJson throws
      // Error(text) without the response object. Also matches the
      // NOT_IN_SCOPE code path.
      if (/not scoped to Sales Demos/i.test(msg)) {
        setOutOfScope(true);
      } else if (!opts.silent) {
        setError(msg);
      }
      // silent + non-403 → keep showing the retained list; the next
      // successful poll self-heals.
    } finally {
      if (!opts.silent) setLoading(false);
    }
  }, []);

  const loadStatus = useCallback(async () => {
    try {
      const s = await apiJson<BootstrapStatus>('/api/sales-demos/bootstrap-status');
      setStatus(s);
    } catch {
      // Non-fatal — bootstrap panel just won't render.
    }
  }, []);

  useEffect(() => {
    loadBrands();
    loadStatus();
  }, [loadBrands, loadStatus]);

  // Auto-refresh brands every 8s so inFlightDetectRuns stays live.
  // Skipped while outOfScope (403) or on the bootstrap panel to avoid
  // noisy 403 loops. Interval clears on unmount.
  useEffect(() => {
    if (outOfScope) return;
    const id = window.setInterval(() => { void loadBrands({ silent: true }); }, 8000);
    return () => window.clearInterval(id);
  }, [outOfScope, loadBrands]);

  const handleBootstrap = async () => {
    setBootstrapping(true);
    try {
      const r = await apiJson<{ advertiserId: string; advertiserName: string }>(
        '/api/sales-demos/bootstrap',
        { method: 'POST' }
      );
      toast({ status: 'success', title: 'Sales Demos workspace ready', description: 'Switching workspace…' });
      // Reload into the Sales Demos advertiser context. setActiveAdvertiser
      // persists to localStorage + triggers a full reload (see BrandContext).
      setActiveAdvertiser(r.advertiserId);
    } catch (err) {
      toast({ status: 'error', title: 'Bootstrap failed', description: (err as Error).message });
      setBootstrapping(false);
    }
  };

  const handleCreate = async () => {
    if (!newName.trim()) {
      toast({ status: 'warning', title: 'Name is required' });
      return;
    }
    setCreating(true);
    try {
      await apiJson('/api/sales-demos/brands', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          name:       newName.trim(),
          igHandle:   newIg.trim()      || null,
          shopifyUrl: newShopify.trim() || null,
          method:     newMethod
        })
      });
      setNewName(''); setNewIg(''); setNewShopify('');
      toast({ status: 'success', title: 'Demo brand created' });
      await loadBrands();
    } catch (err) {
      toast({ status: 'error', title: 'Create failed', description: (err as Error).message });
    } finally {
      setCreating(false);
    }
  };

  const handleSync = async (brandId: string) => {
    // Apify pulls routinely exceed Netlify's ~30s proxy timeout, so
    // the backend kicks off the sync in the background (202) and the
    // frontend polls the brand list until Brand.apifyDemo.lastSyncedAt
    // bumps. Capture the pre-sync timestamp for the completion check.
    const brand  = brands.find(b => b._id === brandId);
    const before = brand?.apifyDemo?.lastSyncedAt ?? null;
    const method = brand?.apifyDemo?.method ?? 'shopify-direct';
    abortRef.current[brandId] = false;
    setSyncing(s => ({ ...s, [brandId]: true }));
    try {
      await apiJson(`/api/sales-demos/brands/${brandId}/sync`, { method: 'POST' });
      toast({
        status: 'info',
        title: 'Sync started',
        description: method === 'apify'
          ? 'Pulling from Apify. This can take 1–3 minutes.'
          : method === 'generic-sitemap'
            ? 'Scanning the store’s sitemaps for products + posts from Apify…'
            : 'Pulling catalog from Shopify + posts from Apify…'
      });
      pollForSyncCompletion(brandId, before);
    } catch (err) {
      toast({ status: 'error', title: 'Sync failed', description: (err as Error).message });
      setSyncing(s => ({ ...s, [brandId]: false }));
    }
  };

  const handleEnrich = async (brandId: string) => {
    setEnriching(s => ({ ...s, [brandId]: true }));
    try {
      await apiJson(`/api/sales-demos/brands/${brandId}/enrich`, { method: 'POST' });
      toast({
        status: 'info',
        title: 'Enrichment started',
        description: 'Fetching cross-seller pricing + web-wide reviews. Watch the Activity log below for progress; you can Stop it there.'
      });
    } catch (err) {
      const msg = (err as Error).message;
      toast({
        status: /already running/i.test(msg) ? 'warning' : 'error',
        title:  /already running/i.test(msg) ? 'Already enriching' : 'Enrich failed',
        description: msg
      });
    } finally {
      // Clear the button spinner shortly after the 202 — progress lives in
      // the Activity log / dock, not this button.
      setTimeout(() => setEnriching(s => ({ ...s, [brandId]: false })), 1500);
    }
  };

  const handleAbort = async (brandId: string) => {
    // Signal the poller to exit silently — the sync's own
    // Brand.apifyDemo.lastSyncedAt bump would otherwise fire a
    // misleading "Sync complete" toast a minute later.
    abortRef.current[brandId] = true;
    try {
      const r = await apiJson<{ cancelledDetectRuns: number }>(
        `/api/sales-demos/brands/${brandId}/abort`,
        { method: 'POST' }
      );
      toast({
        status: 'warning',
        title:  'Sync aborted',
        description: `Cancelled ${r.cancelledDetectRuns} in-flight detect run${r.cancelledDetectRuns === 1 ? '' : 's'}. Ingested records are preserved — hit Sync again to resume detect off the index.`
      });
    } catch (err) {
      toast({ status: 'error', title: 'Abort failed', description: (err as Error).message });
    } finally {
      setSyncing(s => ({ ...s, [brandId]: false }));
      await loadBrands();
    }
  };

  // Poll the brand list every 5s looking for Brand.apifyDemo.lastSyncedAt
  // to advance past `before`. Times out after 5 min — at that point
  // we clear the busy state and let the operator refresh manually.
  const pollForSyncCompletion = (brandId: string, before: string | null) => {
    const startedAt = Date.now();
    const TIMEOUT_MS = 5 * 60 * 1000;
    const INTERVAL_MS = 5000;

    const tick = async () => {
      // Operator hit Abort — bail without a completion toast. The
      // abort handler already surfaced its own "Sync aborted" toast.
      if (abortRef.current[brandId]) return;
      try {
        const data = await apiJson<{ brands: DemoBrand[] }>('/api/sales-demos/brands');
        const b = data.brands.find(x => x._id === brandId);
        const after = b?.apifyDemo?.lastSyncedAt ?? null;
        const bumped = after && after !== before;
        if (bumped) {
          if (abortRef.current[brandId]) return;
          setBrands(data.brands || []);
          setSyncing(s => ({ ...s, [brandId]: false }));
          toast({ status: 'success', title: 'Sync complete', description: 'Records ingested. Detect runs are queued.' });
          return;
        }
      } catch {
        // Swallow transient errors — the next tick retries. A hard
        // failure (network gone, auth lost) surfaces via apiFetch's
        // own redirect behavior.
      }
      if (Date.now() - startedAt > TIMEOUT_MS) {
        setSyncing(s => ({ ...s, [brandId]: false }));
        toast({
          status: 'warning',
          title: 'Sync still running',
          description: 'Taking longer than expected. Refresh in a few minutes.'
        });
        return;
      }
      setTimeout(tick, INTERVAL_MS);
    };
    setTimeout(tick, INTERVAL_MS);
  };

  if (outOfScope) {
    return (
      <VStack align="stretch" spacing={6}>
        <PageHeader
          title="Sales Demos"
          description="Internal-only. Create demo Brands and pull public records straight from the store (free) or via Apify without waiting for a prospect to complete OAuth."
        />
        <Card>
          <CardBody>
            {status?.canBootstrap ? (
              <VStack align="start" spacing={4}>
                <Heading size="sm">Set up Sales Demos workspace</Heading>
                <Text fontSize="sm" color="brand.muted">
                  You're not yet a member of the Sales Demos workspace. Click below to create it and make yourself owner. After setup, you'll be switched into the new workspace and can start creating demo brands.
                </Text>
                <Button variant="brand" onClick={handleBootstrap} isLoading={bootstrapping}>
                  Set up workspace
                </Button>
              </VStack>
            ) : (
              <VStack align="start" spacing={3}>
                <Heading size="sm">Not authorized</Heading>
                <Text fontSize="sm" color="brand.muted">
                  Your account isn't on the Sales Demos allowlist. Ask an admin to add your email to <code>SALES_DEMOS_ADMINS</code> in the backend environment.
                </Text>
              </VStack>
            )}
          </CardBody>
        </Card>
      </VStack>
    );
  }

  return (
    <VStack align="stretch" spacing={6}>
      <PageHeader
        title="Sales Demos"
        description="Internal-only. Create demo Brands and pull public records straight from the store (free) or via Apify without waiting for a prospect to complete OAuth."
      />

      <Card>
        <CardBody>
          <Heading size="sm" mb={4}>New demo brand</Heading>
          <RadioGroup
            value={newMethod}
            onChange={(v) => setNewMethod(v as DemoMethod)}
            mb={4}
          >
            <VStack align="stretch" spacing={2}>
              <Box
                as="label"
                borderWidth="1px"
                borderRadius="md"
                borderColor={newMethod === 'shopify-direct' ? 'rsViolet.400' : 'brand.border'}
                bg={newMethod === 'shopify-direct' ? 'rsViolet.50' : 'brand.surface'}
                p={3}
                cursor="pointer"
              >
                <HStack align="start" spacing={3}>
                  <Radio value="shopify-direct" mt={0.5} />
                  <Box>
                    <Text fontSize="sm" fontWeight="medium">Shopify direct + Instagram</Text>
                    <Text fontSize="xs" color="brand.muted">
                      Catalog, reviews & videos from the store's public endpoints (free) · IG posts via Apify
                    </Text>
                  </Box>
                </HStack>
              </Box>
              <Box
                as="label"
                borderWidth="1px"
                borderRadius="md"
                borderColor={newMethod === 'apify' ? 'rsViolet.400' : 'brand.border'}
                bg={newMethod === 'apify' ? 'rsViolet.50' : 'brand.surface'}
                p={3}
                cursor="pointer"
              >
                <HStack align="start" spacing={3}>
                  <Radio value="apify" mt={0.5} />
                  <Box>
                    <Text fontSize="sm" fontWeight="medium">Full Apify scrape</Text>
                    <Text fontSize="xs" color="brand.muted">
                      Catalog and IG both via paid Apify actors
                    </Text>
                  </Box>
                </HStack>
              </Box>
              <Box
                as="label"
                borderWidth="1px"
                borderRadius="md"
                borderColor={newMethod === 'generic-sitemap' ? 'rsViolet.400' : 'brand.border'}
                bg={newMethod === 'generic-sitemap' ? 'rsViolet.50' : 'brand.surface'}
                p={3}
                cursor="pointer"
              >
                <HStack align="start" spacing={3}>
                  <Radio value="generic-sitemap" mt={0.5} />
                  <Box>
                    <Text fontSize="sm" fontWeight="medium">Generic sitemap + Instagram</Text>
                    <Text fontSize="xs" color="brand.muted">
                      For non-Shopify stores (free): catalog from the site's XML sitemaps + product JSON-LD · IG posts via Apify
                    </Text>
                  </Box>
                </HStack>
              </Box>
            </VStack>
          </RadioGroup>
          <SimpleGrid columns={{ base: 1, md: 4 }} spacing={3} alignItems="end">
            <FormControl>
              <FormLabel fontSize="xs" color="brand.muted">Brand name</FormLabel>
              <Input value={newName} onChange={e => setNewName(e.target.value)} placeholder="Pelagic Gear" />
            </FormControl>
            <FormControl>
              <FormLabel fontSize="xs" color="brand.muted">IG handle</FormLabel>
              <Input value={newIg} onChange={e => setNewIg(e.target.value)} placeholder="@pelagicgear" />
            </FormControl>
            <FormControl>
              <FormLabel fontSize="xs" color="brand.muted">
                {newMethod === 'generic-sitemap' ? 'Store / catalog URL' : 'Shopify URL'}
              </FormLabel>
              <Input value={newShopify} onChange={e => setNewShopify(e.target.value)} placeholder="https://pelagicgear.com" />
            </FormControl>
            <Button variant="brand" onClick={handleCreate} isLoading={creating}>Create</Button>
          </SimpleGrid>
        </CardBody>
      </Card>

      {loading ? (
        <Card><CardBody><HStack><Spinner size="sm" /><Text>Loading demo brands…</Text></HStack></CardBody></Card>
      ) : error ? (
        <Card><CardBody><Text color="red.500">{error}</Text></CardBody></Card>
      ) : brands.length === 0 ? (
        <Card><CardBody><Text color="brand.muted">No demo brands yet. Create one above.</Text></CardBody></Card>
      ) : (
        <VStack align="stretch" spacing={3}>
          {brands.map(b => (
            <DemoBrandCard
              key={b._id}
              brand={b}
              syncing={!!syncing[b._id]}
              enriching={!!enriching[b._id]}
              hasInFlightRuns={b.inFlightDetectRuns > 0}
              onSync={() => handleSync(b._id)}
              onAbort={() => handleAbort(b._id)}
              onEnrich={() => handleEnrich(b._id)}
            />
          ))}
        </VStack>
      )}

      <ActivityLog brands={brands} />
    </VStack>
  );
}

// ── Activity log ─────────────────────────────────────────────────────
// Cross-brand view of the OperationRun feed for the Sales Demos
// workspace — every instrumented process (sync, enrichment, detect, ad
// generation, video, …), active first then recently finished. At-a-glance
// "what is the system working on right now". Polls every 3s.

type ActivityRun = {
  _id: string; kind: string; label: string | null; status: string;
  stage: string | null; note: string | null; pct: number | null;
  itemsDone: number; itemsTotal: number | null; brandId: string | null;
  cancellable: boolean; error: string | null;
  startedAt: string; endedAt: string | null; heartbeatAt: string | null;
};

const KIND_LABELS: Record<string, string> = {
  'catalog-sync': 'Catalog sync', 'social-ingest': 'Social posts', 'demo-sync': 'Demo data sync',
  enrichment: 'Enrichment', 'category-inference': 'Category inference',
  'font-ingest': 'Font ingest', 'campaign-sync': 'Campaign sync',
  'scheduled-sync': 'Scheduled sync', 'ad-batch': 'Ad generation', 'ad-regenerate': 'Ad regenerate',
  'veo-video': 'Video generation', render: 'Render', detect: 'Media detect', 'ai-layout': 'AI layouts',
};

const STATUS_COLOR: Record<string, string> = {
  running: 'purple', cancelling: 'orange', succeeded: 'green', failed: 'red', cancelled: 'gray',
};

function runLabelOf(r: ActivityRun) {
  return r.label || KIND_LABELS[r.kind] || r.kind;
}

function fmtElapsed(startedAt: string, endedAt: string | null) {
  const end = endedAt ? new Date(endedAt).getTime() : Date.now();
  const s = Math.max(0, Math.floor((end - new Date(startedAt).getTime()) / 1000));
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  return `${m}m ${s % 60}s`;
}

function ActivityLog({ brands }: { brands: DemoBrand[] }) {
  const [active, setActive] = useState<ActivityRun[]>([]);
  const [recent, setRecent] = useState<ActivityRun[]>([]);
  const [stopping, setStopping] = useState<Record<string, boolean>>({});

  const nameById = useMemo(() => {
    const m: Record<string, string> = {};
    for (const b of brands) m[b._id] = b.name;
    return m;
  }, [brands]);

  const fetchActivity = useCallback(async () => {
    const d = await apiJson<{ active: ActivityRun[]; recent: ActivityRun[] }>(
      '/api/sales-demos/activity'
    ).catch(() => null);
    if (d) { setActive(d.active || []); setRecent(d.recent || []); }
  }, []);

  useEffect(() => {
    let stopped = false;
    const tick = async () => { if (!stopped) await fetchActivity(); };
    tick();
    const id = window.setInterval(tick, 3000);
    return () => { stopped = true; window.clearInterval(id); };
  }, [fetchActivity]);

  const stopRun = async (id: string) => {
    setStopping(s => ({ ...s, [id]: true }));
    try {
      await apiJson(`/api/progress/${id}/cancel`, { method: 'POST' });
      await fetchActivity();
    } catch {
      setStopping(s => ({ ...s, [id]: false }));
    }
  };

  if (!active.length && !recent.length) return null;

  const brandLabel = (r: ActivityRun) =>
    r.brandId ? (nameById[r.brandId] || 'brand') : null;

  return (
    <Card>
      <CardBody>
        <HStack justify="space-between" mb={3}>
          <Heading size="sm">Activity</Heading>
          <Text fontSize="xs" color="brand.muted">
            {active.length ? `${active.length} running` : 'idle'} · updates live
          </Text>
        </HStack>

        {active.length > 0 && (
          <VStack align="stretch" spacing={2} mb={recent.length ? 4 : 0}>
            {active.map(r => {
              const pct = r.pct != null
                ? Math.round(r.pct * 100)
                : (r.itemsTotal ? Math.round((r.itemsDone / r.itemsTotal) * 100) : null);
              const stoppingThis = r.status === 'cancelling' || stopping[r._id];
              return (
                <Box key={r._id} borderWidth="1px" borderColor="brand.border" borderRadius="md" p={2}>
                  <HStack justify="space-between" align="start" spacing={3}>
                    <HStack spacing={2} minW={0}>
                      <Spinner size="xs" color="rsViolet.500" flexShrink={0} />
                      <Box minW={0}>
                        <Text fontSize="sm" fontWeight="medium" noOfLines={1}>
                          {runLabelOf(r)}
                          {brandLabel(r) && (
                            <Text as="span" color="brand.muted" fontWeight="normal"> · {brandLabel(r)}</Text>
                          )}
                        </Text>
                        <Text fontSize="xs" color="brand.muted" noOfLines={1}>
                          {r.stage || 'working…'}{r.note ? ` — ${r.note}` : ''}
                        </Text>
                      </Box>
                    </HStack>
                    <HStack spacing={2} flexShrink={0}>
                      <Text fontSize="xs" fontFamily="mono" color="brand.muted">
                        {r.itemsTotal ? `${r.itemsDone}/${r.itemsTotal}` : (pct != null ? `${pct}%` : '')}
                      </Text>
                      <Text fontSize="xs" color="brand.muted">{fmtElapsed(r.startedAt, null)}</Text>
                      {r.cancellable && (
                        <Button size="xs" variant="outline" colorScheme="red"
                          isDisabled={stoppingThis} onClick={() => stopRun(r._id)}>
                          {stoppingThis ? 'stopping…' : 'Stop'}
                        </Button>
                      )}
                    </HStack>
                  </HStack>
                  <Progress mt={1.5} size="xs" borderRadius="full" colorScheme="purple"
                    value={pct ?? undefined} isIndeterminate={pct == null} />
                </Box>
              );
            })}
          </VStack>
        )}

        {recent.length > 0 && (
          <>
            {active.length > 0 && <Divider mb={2} />}
            <Text fontSize="xs" color="brand.muted" mb={1}>Recent</Text>
            <VStack align="stretch" spacing={1}>
              {recent.map(r => (
                <HStack key={r._id} justify="space-between" fontSize="xs">
                  <HStack spacing={2} minW={0}>
                    <Badge colorScheme={STATUS_COLOR[r.status] || 'gray'} fontSize="0.6rem">
                      {r.status}
                    </Badge>
                    <Text noOfLines={1}>
                      {runLabelOf(r)}
                      {brandLabel(r) && <Text as="span" color="brand.muted"> · {brandLabel(r)}</Text>}
                    </Text>
                  </HStack>
                  <Text color="brand.muted" flexShrink={0}>
                    {r.error ? r.error.slice(0, 40) : fmtElapsed(r.startedAt, r.endedAt)}
                  </Text>
                </HStack>
              ))}
            </VStack>
          </>
        )}
      </CardBody>
    </Card>
  );
}

// Live per-brand sync progress. Polls the unified OperationRun feed
// scoped to this brand and renders the current stage + a running count
// (IG posts during the Instagram phase, products during the catalog
// scan) + the resolver's live note ("scanned N · found M"). Only shown
// while a run is active for the brand.
function SyncProgress({ brandId }: { brandId: string }) {
  const [run, setRun] = useState<OperationRunDto | null>(null);

  useEffect(() => {
    let stopped = false;
    const fetchRun = async () => {
      const r = await apiJson<{ runs: OperationRunDto[] }>(
        `/api/progress/active?brandId=${encodeURIComponent(brandId)}`
      ).catch(() => null);
      if (stopped) return;
      if (r && Array.isArray(r.runs)) {
        // Feed is newest-first; prefer the demo-sync run for this brand.
        setRun(r.runs.find(x => x.kind === 'demo-sync') || r.runs[0] || null);
      }
    };
    fetchRun();
    const id = window.setInterval(fetchRun, 2000);
    return () => { stopped = true; window.clearInterval(id); };
  }, [brandId]);

  if (!run) return null;
  if (run.status && !['running', 'cancelling'].includes(run.status)) return null;

  const done = run.itemsDone ?? 0;
  const total = run.itemsTotal ?? null;
  const pct = run.pct != null
    ? Math.round(run.pct * 100)
    : (total ? Math.round((done / total) * 100) : null);
  const stage = run.stage || 'working…';
  const s = stage.toLowerCase();
  // Phase-aware count label.
  const count = /instagram|post/.test(s)
    ? `${done} post${done === 1 ? '' : 's'}`
    : /product|catalog|scan|sitemap/.test(s)
      ? `${done}${total ? ` / ${total}` : ''} products`
      : (total ? `${done} / ${total}` : `${done}`);

  return (
    <Box mt={3} pt={3} borderTopWidth="1px" borderColor="brand.border">
      <HStack justify="space-between" mb={1}>
        <Text fontSize="xs" fontWeight="medium" textTransform="capitalize">{stage}</Text>
        <Text fontSize="xs" color="brand.muted">{count}</Text>
      </HStack>
      <Progress
        size="xs"
        borderRadius="full"
        colorScheme="purple"
        value={pct ?? undefined}
        isIndeterminate={pct == null}
      />
      {run.note && <Text fontSize="2xs" color="brand.muted" mt={1}>{run.note}</Text>}
    </Box>
  );
}

function DemoBrandCard({
  brand, syncing, enriching, hasInFlightRuns, onSync, onAbort, onEnrich
}: {
  brand:           DemoBrand;
  syncing:         boolean;
  enriching:       boolean;
  hasInFlightRuns: boolean;
  onSync:          () => void;
  onAbort:         () => void;
  onEnrich:        () => void;
}) {
  const cfg = brand.apifyDemo || {};
  const configured = cfg.igHandle || cfg.shopifyUrl;
  const method = cfg.method ?? 'shopify-direct';
  // "Something is in flight" = either this session started a sync
  // (local syncing state) OR the server reports queued/processing
  // DetectRuns for this brand. Persists across page navigations.
  const inFlight = syncing || hasInFlightRuns;
  return (
    <Card>
      <CardBody>
        <HStack justify="space-between" align="start">
          <Box>
            <HStack mb={1}>
              <Heading size="sm">{brand.name}</Heading>
              <Badge colorScheme="purple">demo</Badge>
            </HStack>
            <VStack align="start" spacing={0.5} fontSize="xs" color="brand.muted">
              <HStack spacing={1}>
                <Badge
                  colorScheme={method === 'apify' ? 'purple' : method === 'generic-sitemap' ? 'blue' : 'teal'}
                  fontSize="0.65rem"
                >
                  {method === 'apify' ? 'Apify' : method === 'generic-sitemap' ? 'Generic sitemap' : 'Shopify direct'}
                </Badge>
              </HStack>
              {cfg.igHandle && <Text>IG: @{cfg.igHandle}</Text>}
              {cfg.shopifyUrl && <Text>{method === 'generic-sitemap' ? 'Store' : 'Shopify'}: {cfg.shopifyUrl}</Text>}
              {((brand.productCount ?? 0) > 0 || (brand.postCount ?? 0) > 0) && (
                <HStack spacing={2} pt={0.5}>
                  {(brand.productCount ?? 0) > 0 && (
                    <Badge colorScheme="green" variant="subtle" fontSize="0.65rem">
                      {brand.productCount!.toLocaleString()} product{brand.productCount === 1 ? '' : 's'}
                    </Badge>
                  )}
                  {(brand.postCount ?? 0) > 0 && (
                    <Badge colorScheme="pink" variant="subtle" fontSize="0.65rem">
                      {brand.postCount!.toLocaleString()} post{brand.postCount === 1 ? '' : 's'}
                    </Badge>
                  )}
                  {(brand.productCount ?? 0) > 0 && (
                    // Review coverage = products with review data / total products.
                    <Badge colorScheme="purple" variant="subtle" fontSize="0.65rem">
                      {Math.round(((brand.reviewedProductCount ?? 0) / brand.productCount!) * 100)}% review coverage
                    </Badge>
                  )}
                </HStack>
              )}
              {cfg.lastSyncedAt && <Text>Last sync: {new Date(cfg.lastSyncedAt).toLocaleString()}</Text>}
              {!configured && <Text color="orange.400">No sources configured — edit brand to add IG / Shopify.</Text>}
            </VStack>
          </Box>
          <HStack spacing={2}>
            {inFlight && (
              <Button
                size="sm"
                variant="outline"
                colorScheme="red"
                onClick={onAbort}
              >
                Abort{hasInFlightRuns && !syncing ? ` (${brand.inFlightDetectRuns})` : ''}
              </Button>
            )}
            <Button
              size="sm"
              variant="brand"
              onClick={onSync}
              isLoading={syncing}
              isDisabled={!configured || inFlight}
            >
              {method === 'apify' ? 'Sync from Apify' : 'Sync store + IG'}
            </Button>
            {/* Enrich = paid cross-seller pricing + web-wide reviews. Off the
                sync path (opt-in). Enabled once products exist and nothing's
                in flight. Progress shows in the Activity log + global dock. */}
            <Button
              size="sm"
              variant="outline"
              onClick={onEnrich}
              isLoading={enriching}
              isDisabled={(brand.productCount ?? 0) === 0 || inFlight}
              title="Fetch cross-seller pricing + web-wide reviews for this brand's products"
            >
              Enrich
            </Button>
          </HStack>
        </HStack>
        {inFlight && <SyncProgress brandId={brand._id} />}
      </CardBody>
    </Card>
  );
}
